import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useSort } from '../lib/sort';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import { Inbox, Check, X, FileText, Eye, Trash2, Cpu, Sparkles, UploadCloud, Mail, Copy } from 'lucide-react';

const Innboks = ({ embed = false }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('ny');
  const [data, setData] = useState([]);
  const [valgt, setValgt] = useState(null);
  const [bildeUrl, setBildeUrl] = useState('');
  const [skjema, setSkjema] = useState({});
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [forslag, setForslag] = useState(null);
  const [innboksEpost, setInnboksEpost] = useState('');
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    if (embed) return;
    api.get('/profil/innboks-adresse').then((r) => setInnboksEpost(r.data?.adresse || '')).catch(() => {});
  }, [embed]);

  const kopierEpost = () => {
    if (!innboksEpost) return;
    navigator.clipboard?.writeText(innboksEpost).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    });
  };

  const last = useCallback(() => {
    api.get('/innboks', { params: { status: filter } }).then((r) => setData(r.data));
  }, [filter]);
  useEffect(() => { last(); }, [last]);

  const apne = (i) => {
    setValgt(i);
    let ocr = {};
    try { ocr = i.ocr_data ? JSON.parse(i.ocr_data) : {}; } catch { ocr = {}; }
    setSkjema({
      dato: ocr.dato || '',
      beskrivelse: ocr.beskrivelse || ocr.leverandor || i.notat || '',
      kategori: ocr.kategori || '',
      sum: ocr.sum || '',
      mva: ocr.mva || '',
    });
    // hent bilde via fetch (krever JWT)
    const tok = localStorage.getItem('token');
    fetch(`${api.defaults.baseURL}/innboks/${i.id}/bilde`, {
      headers: { Authorization: `Bearer ${tok}` },
    }).then((r) => r.blob()).then((b) => setBildeUrl(URL.createObjectURL(b)));
    // Auto-kjør OCR hvis den mangler (f.eks. opplastet via desktop før nøkkel var satt)
    if (!i.ocr_data) {
      setBusy(true);
      api.post(`/innboks/${i.id}/ocr`).then((r) => {
        const ny = r.data;
        let o2 = {};
        try { o2 = ny.ocr_data ? JSON.parse(ny.ocr_data) : {}; } catch {}
        setValgt(ny);
        setSkjema((s) => ({
          dato: o2.dato || s.dato,
          beskrivelse: o2.beskrivelse || o2.leverandor || s.beskrivelse,
          kategori: o2.kategori || s.kategori,
          sum: o2.sum != null ? o2.sum : s.sum,
          mva: o2.mva != null ? o2.mva : s.mva,
        }));
      }).catch(() => {}).finally(() => setBusy(false));
    }
  };

  const lukk = () => {
    if (bildeUrl) URL.revokeObjectURL(bildeUrl);
    setValgt(null); setBildeUrl(''); setSkjema({}); setForslag(null);
  };

  // Hent kontoforslag basert på beskrivelse + kategori (debounced)
  useEffect(() => {
    if (!valgt) { setForslag(null); return; }
    const tekst = (skjema.beskrivelse || '').trim();
    if (!tekst && !skjema.kategori) { setForslag(null); return; }
    const t = setTimeout(() => {
      api.get('/forslag/konto', {
        params: { type: 'utgift', kategori: skjema.kategori || '', beskrivelse: tekst },
      }).then((r) => setForslag(r.data)).catch(() => setForslag(null));
    }, 250);
    return () => clearTimeout(t);
  }, [valgt, skjema.beskrivelse, skjema.kategori]);

  const lagBilag = async () => {
    if (!valgt) return;
    setBusy(true);
    try {
      const r = await api.post(`/innboks/${valgt.id}/til-bilag`, {
        dato: skjema.dato, beskrivelse: skjema.beskrivelse,
        kategori: skjema.kategori,
        sum: skjema.sum,
        mva: skjema.mva,
      });
      const bid = r.data.bilag_id;
      const apne = window.confirm(
        `Bilag ${r.data.bilagsnummer} opprettet.\n\n` +
        `Vil du åpne det nå for å sjekke konto/kategori før det låses?\n\n` +
        `(Du kan også rette siste bilag senere fra Bilag-listen.)`
      );
      lukk(); last();
      if (apne && bid) navigate(`/bilag?rediger=${bid}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Feil.');
    } finally { setBusy(false); }
  };

  const forkast = async (i) => {
    if (!window.confirm('Forkaste denne skannen?')) return;
    await api.patch(`/innboks/${i.id}`, { status: 'forkastet' });
    last();
  };

  const slett = async (i) => {
    if (!window.confirm('Slett permanent?')) return;
    await api.delete(`/innboks/${i.id}`);
    last();
  };

  const kjorOcrPaaNytt = async () => {
    if (!valgt) return;
    setBusy(true);
    try {
      const r = await api.post(`/innboks/${valgt.id}/ocr`);
      const i = r.data;
      let ocr = {};
      try { ocr = i.ocr_data ? JSON.parse(i.ocr_data) : {}; } catch {}
      setValgt(i);
      setSkjema({
        dato: ocr.dato || skjema.dato || '',
        beskrivelse: ocr.beskrivelse || ocr.leverandor || skjema.beskrivelse || '',
        kategori: ocr.kategori || skjema.kategori || '',
        sum: ocr.sum != null ? ocr.sum : (skjema.sum || ''),
        mva: ocr.mva != null ? ocr.mva : (skjema.mva || ''),
      });
      if (i.ocr_advarsel) alert('OCR fullført med advarsel: ' + i.ocr_advarsel);
    } catch (e) {
      alert('OCR feilet: ' + (e.response?.data?.error || e.message));
    } finally { setBusy(false); }
  };

  const sortAccInn = {
    filnavn: (i) => i.filnavn || '',
    notat: (i) => i.notat || '',
    ocr: (i) => { try { return (JSON.parse(i.ocr_data || '{}').leverandor || ''); } catch { return ''; } },
    status: (i) => i.status,
    opplastet: (i) => i.opplastet || '',
  };
  const { sorted: radSortert, Th: ThInn } = useSort(data, { defaultKey: 'opplastet', defaultDir: 'desc', accessors: sortAccInn });

  // ── Dra-og-slipp / desktop-opplasting ────────────────────────────────────
  const filRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [opplastBusy, setOpplastBusy] = useState(false);

  const lastOppFiler = async (filer) => {
    const liste = Array.from(filer || []).filter(Boolean);
    if (liste.length === 0) return;
    setOpplastBusy(true);
    let ok = 0, feil = 0;
    for (const f of liste) {
      const fd = new FormData();
      fd.append('fil', f);
      try {
        await api.post('/innboks', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch { feil++; }
    }
    setOpplastBusy(false);
    if (filRef.current) filRef.current.value = '';
    last();
    if (feil) alert(`${ok} lastet opp, ${feil} feilet.`);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    lastOppFiler(e.dataTransfer?.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  // Lim inn bilde fra utklippstavle (Ctrl+V) når siden er aktiv
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const filer = [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) filer.push(f);
        }
      }
      if (filer.length) lastOppFiler(filer);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {!embed && (
        <PageHeader
          title="Innboks"
          subtitle="Skannede kvitteringer fra mobil – behandle og lag bilag"
        />
      )}

      {!embed && innboksEpost && (
        <Card style={{ marginBottom: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Mail size={18} style={{ color: 'var(--theme-primary, #4ea1ff)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted, #888)' }}>Din personlige epost-til-bilag</div>
            <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 14 }}>{innboksEpost}</div>
            <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #888)', marginTop: 2 }}>
              Videresend leverandør-epost hit — vedlegg havner automatisk i Innboks som nye bilag.
            </div>
          </div>
          <button className="btn-ghost" onClick={kopierEpost} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Copy size={14} /> {kopiert ? 'Kopiert!' : 'Kopier'}
          </button>
        </Card>
      )}

      <Tabs
        value={filter}
        onChange={setFilter}
        items={[
          { key: 'ny',         label: 'Nye' },
          { key: 'behandlet',  label: 'Behandlet' },
          { key: 'forkastet',  label: 'Forkastet' },
          { key: 'alle',       label: 'Alle' },
        ]}
      />


      {/* Dra-og-slipp opplasting (desktop) – skjules i embed (Bilag-siden har egen knapp) */}
      {!embed && (
      <Card
        style={{
          marginBottom: 12, padding: 20,
          border: dragOver ? '2px dashed var(--theme-primary, #4ea1ff)' : '2px dashed var(--theme-border-light, #2222)',
          background: dragOver ? 'var(--theme-primary-bg, rgba(78,161,255,0.08))' : 'transparent',
          cursor: 'pointer', transition: 'all 0.15s',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="flex items-center" style={{ gap: 12 }}>
          <UploadCloud size={28} style={{ color: 'var(--theme-primary, #4ea1ff)', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              {opplastBusy ? 'Laster opp…' : 'Last opp kvitteringer, PDF-er eller bilder'}
            </div>
            <div className="fm-text-muted" style={{ fontSize: 11, marginTop: 2 }}>
              Dra og slipp filer her, lim inn (Ctrl+V), eller klikk «Velg filer».
            </div>
          </div>
        </div>
        <button
          className="btn-primary"
          onClick={(e) => { e.stopPropagation(); filRef.current?.click(); }}
          disabled={opplastBusy}
          style={{ flexShrink: 0 }}
        >
          <UploadCloud size={14} /> Velg filer
        </button>
        <input
          ref={filRef}
          type="file"
          accept="image/*,.pdf"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => lastOppFiler(e.target.files)}
        />
      </Card>
      )}

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              {ThInn('filnavn', 'Filnavn')}
              {ThInn('notat', 'Notat')}
              {ThInn('ocr', 'OCR')}
              {ThInn('status', 'Status')}
              {ThInn('opplastet', 'Opplastet')}
              <th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {radSortert.length === 0 && (
              <tr><td colSpan={6} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>
                <Inbox size={20} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Ingen skanninger med status «{filter}».
              </td></tr>
            )}
            {radSortert.map((i) => {
              let ocr = {};
              try { ocr = i.ocr_data ? JSON.parse(i.ocr_data) : {}; } catch {}
              return (
                <tr key={i.id}>
                  <td><FileText size={12} /> {i.filnavn || `Skann #${i.id}`}</td>
                  <td className="fm-text-muted">{i.notat || '—'}</td>
                  <td style={{ fontSize: 11 }}>
                    {ocr.leverandor && <div>{ocr.leverandor}</div>}
                    {ocr.sum != null && <div className="fm-text-muted">kr {ocr.sum}</div>}
                  </td>
                  <td>
                    <span className={`badge ${i.status === 'behandlet' ? 'badge-success' :
                                              i.status === 'forkastet' ? 'badge-danger' : 'badge-neutral'}`}>
                      {i.status}
                    </span>
                  </td>
                  <td>{i.opplastet?.slice(0, 16).replace('T', ' ')}</td>
                  <td>
                    <div className="flex" style={{ gap: 4 }}>
                      <button className="btn-ghost" title="Behandle" onClick={() => apne(i)}>
                        <Eye size={12} />
                      </button>
                      {i.status === 'ny' && (
                        <button className="btn-ghost" title="Forkast" onClick={() => forkast(i)}>
                          <X size={12} />
                        </button>
                      )}
                      <button className="btn-ghost" title="Slett"
                              onClick={() => slett(i)}
                              style={{ color: 'var(--theme-danger)' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Modal
        open={!!valgt} onClose={lukk} width={780}
        title={`Behandle skann ${valgt?.filnavn ? '– ' + valgt.filnavn : ''}`}
        footer={<>
          <button className="btn-ghost" onClick={kjorOcrPaaNytt} disabled={busy} title="Kjør OCR på nytt">
            <Cpu size={14} /> {busy ? 'Kjører…' : 'Kjør OCR på nytt'}
          </button>
          <button className="btn-ghost" onClick={lukk}>Lukk</button>
          <button className="btn-primary" onClick={lagBilag} disabled={busy}>
            <Check size={14} /> {busy ? 'Lagrer…' : 'Lag bilag'}
          </button>
        </>}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            {bildeUrl ? (
              (valgt?.mime === 'application/pdf' || /\.pdf$/i.test(valgt?.filnavn || '')) ? (
                <iframe src={bildeUrl} title="pdf"
                        style={{ width: '100%', height: 480, border: 'none',
                                 background: '#000', borderRadius: 6 }} />
              ) : (
                <img src={bildeUrl} alt="kvittering"
                     onClick={() => setZoom(true)}
                     title="Klikk for å zoome"
                     style={{ width: '100%', maxHeight: 480, objectFit: 'contain',
                              background: '#000', borderRadius: 6, cursor: 'zoom-in' }} />
              )
            ) : <div className="fm-text-muted">Laster forhåndsvisning…</div>}
          </div>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label>Dato</label>
              <input type="date" value={skjema.dato || ''}
                     onChange={(e) => setSkjema({...skjema, dato: e.target.value})} /></div>
            <div><label>Kategori</label>
              <input value={skjema.kategori || ''}
                     onChange={(e) => setSkjema({...skjema, kategori: e.target.value})} /></div>
            {forslag && (forslag.kategori?.length > 0 || forslag.forslag_kategori) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="fm-text-muted" style={{ fontSize: 11, marginBottom: 4,
                                                         display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Sparkles size={12} /> Forslag (klikk for å bruke):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(forslag.kategori || []).slice(0, 6).map((k, i) => (
                    <button key={i} type="button" className="btn-ghost"
                            onClick={() => setSkjema({ ...skjema, kategori: k.kategori })}
                            style={{ padding: '2px 8px', fontSize: 12, border: '1px solid var(--theme-border)' }}>
                      {k.kategori}
                      {k.historikk && <span className="badge badge-info"
                                            style={{ marginLeft: 4, fontSize: 10 }}>{k.antall}×</span>}
                    </button>
                  ))}
                  {forslag.forslag_kategori &&
                    !(forslag.kategori || []).some((k) => k.kategori === forslag.forslag_kategori) && (
                    <button type="button" className="btn-ghost"
                            onClick={() => setSkjema({ ...skjema, kategori: forslag.forslag_kategori })}
                            style={{ padding: '2px 8px', fontSize: 12, border: '1px solid var(--theme-border)' }}>
                      {forslag.forslag_kategori}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}><label>Beskrivelse</label>
              <input value={skjema.beskrivelse || ''}
                     onChange={(e) => setSkjema({...skjema, beskrivelse: e.target.value})} /></div>
            <div><label>Sum (inkl. mva)</label>
              <input type="text" inputMode="decimal" placeholder="0,00" value={skjema.sum ?? ''}
                     onChange={(e) => setSkjema({...skjema, sum: e.target.value})} /></div>
            <div><label>Mva</label>
              <input type="text" inputMode="decimal" placeholder="0,00" value={skjema.mva ?? ''}
                     onChange={(e) => setSkjema({...skjema, mva: e.target.value})} /></div>
            <div style={{ gridColumn: '1 / -1', fontSize: 11 }} className="fm-text-muted">
              {valgt?.ocr_data ? 'Forhåndsutfylt fra OCR – sjekk verdiene.' :
                'Ingen OCR-data. Trykk «Kjør OCR på nytt» hvis du har lagt inn OpenAI-nøkkel under Innstillinger.'}
            </div>
          </div>
        </div>
      </Modal>

      {zoom && bildeUrl && (
        <div onClick={() => setZoom(false)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
                      zIndex: 9999, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', cursor: 'zoom-out', padding: 20 }}>
          <button onClick={(e) => { e.stopPropagation(); setZoom(false); }}
                  style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)',
                           border: 'none', color: '#fff', borderRadius: 6, padding: '8px 12px',
                           cursor: 'pointer', fontSize: 14 }}>
            <X size={16} style={{ verticalAlign: 'middle' }} /> Lukk
          </button>
          <img src={bildeUrl} alt="kvittering – stor"
               onClick={(e) => e.stopPropagation()}
               style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain',
                        cursor: 'default', boxShadow: '0 0 40px rgba(0,0,0,0.6)' }} />
        </div>
      )}
    </>
  );
};

export default Innboks;
